import { ApiResponse, PaginatedResponse } from '../types/index';

/**
 * Creates a standardized success response
 * @param data The data to include in the response
 * @returns A formatted API response with success status
 */
export function createSuccessResponse<T>(data: T): ApiResponse<T> {
  return {
    traceId: generateTraceId(),
    success: true,
    errorMessage: null,
    flag: 'success',
    data,
  };
}

/**
 * Creates a standardized error response
 * @param message The error message
 * @param code Optional error code
 * @returns A formatted API response with error status
 */
export function createErrorResponse<T>(message: string, code?: string): ApiResponse<T> {
  return {
    traceId: generateTraceId(),
    success: false,
    errorMessage: message,
    errorCode: code || 'UNKNOWN_ERROR',
    flag: 'fail',
    data: null,
  };
}

/**
 * Creates a standardized paginated response
 * @param items Array of items for the current page
 * @param page Current page number
 * @param pageSize Items per page
 * @param total Total number of items
 * @returns A formatted paginated response
 */
export function createPaginatedResponse<T>(
  items: T[],
  page: number,
  pageSize: number,
  total: number
): ApiResponse<PaginatedResponse<T>> {
  return createSuccessResponse({
    tableData: items,
    paging: {
      currentPage: page,
      pageSize: pageSize,
      totalCount: total,
    },
  });
}

/**
 * Validates if a response object conforms to the ApiResponse structure
 * @param response The object to validate
 * @returns True if the object is a valid ApiResponse
 */
export function isValidApiResponse<T>(response: any): response is ApiResponse<T> {
  return (
    response &&
    typeof response === 'object' &&
    'success' in response &&
    'flag' in response &&
    'data' in response
  );
}

/**
 * Safely extracts data from an API response
 * @param response The API response
 * @returns The data from the response, or null if invalid
 */
export function extractResponseData<T>(response: ApiResponse<T>): T | null {
  if (response.success && response.data !== null) {
    return response.data;
  }
  return null;
}

/**
 * Extracts paginated data from an API response
 * @param response The API response containing paginated data
 * @returns The array of items, or empty array if invalid
 */
export function extractPaginatedData<T>(
  response: ApiResponse<PaginatedResponse<T>>
): T[] {
  if (response.success && response.data && response.data.tableData) {
    return response.data.tableData;
  }
  return [];
}

/**
 * Generates a random trace ID for request tracking
 * @returns A random trace ID string
 */
export function generateTraceId(): string {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
}/**
 * For
mats a timestamp to a readable date string
 * @param timestamp The timestamp in milliseconds
 * @returns Formatted date string in YYYY-MM-DD HH:mm:ss format
 */
export function formatTimestamp(timestamp: number): string {
  if (!timestamp) {
    return '-';
  }
  
  const date = new Date(timestamp);
  
  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return '-';
  }
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

/**
 * Formats a timestamp to a readable date string (date only)
 * @param timestamp The timestamp in milliseconds
 * @returns Formatted date string in YYYY-MM-DD format
 */
export function formatDate(timestamp: number): string {
  if (!timestamp) {
    return '-';
  }
  
  const date = new Date(timestamp);
  
  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return '-';
  }
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
}

/**
 * Formats a timestamp to a relative time string (e.g., "2 hours ago")
 * @param timestamp The timestamp in milliseconds
 * @returns Relative time string
 */
export function formatRelativeTime(timestamp: number): string {
  if (!timestamp) {
    return '-';
  }
  
  const date = new Date(timestamp);
  
  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return '-';
  }
  
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffSeconds < 60) {
    return '刚刚';
  } else if (diffMinutes < 60) {
    return `${diffMinutes}分钟前`;
  } else if (diffHours < 24) {
    return `${diffHours}小时前`;
  } else if (diffDays < 7) {
    return `${diffDays}天前`;
  } else {
    return formatDate(timestamp);
  }
}

// Handle response with error logging
export const handleApiResponse = <T>(
    response: ApiResponse<T>,
    context?: string
): T => {
  if (!response.success) {
    const error = {
      message: response.errorMessage || 'Unknown error',
      code: response.errorCode,
      traceId: response.traceId,
      context
    };

    console.error('API Error:', error);
    throw new Error(response.errorMessage || 'API request failed');
  }

  if (response.data === null || response.data === undefined) {
    console.warn('API returned null data:', { traceId: response.traceId, context });
  }

  return response.data as T;
};